import React, { useEffect, useState } from 'react';
import { Icon } from '../../../../../concepts/resources/Icon';
import { Button, Colors, Input } from '../../../../../ui';
import { ResultInfoForMutation } from '../../../../../ui/ResultInfo';
import { AppNavigator } from '../../../../AppNavigator';
import { useResolve } from '../../../../Container';
import { Copy, Flexer, Title } from '../../../../dashboard/elements/LayoutElements';
import { useSingleSignOnConstants, useSingleSignOnForm, useSingleSignOnMutation } from '../SingleSignOnHooks';
import { CommonFinalStep } from './CommonFinalStep';
import { InputCopyButton } from './InputCopyButton';
import { SetupStep, StepProps } from './SetupStep';
import { SetupStepViewer } from './SetupStepViewer';

const CreateAppStep: React.FC<StepProps> = ({ gotoStep }) => {
    return (
        <SetupStep title="Step 1 — Create Identity Provider Application">
            <Copy flush>
                <p>In your Identity Provider (IDP) admin dashboard:</p>
                <ul>
                    <li>Locate the section for creating new applications or integrations (often called &ldquo;Applications&rdquo;, &ldquo;Integrations&rdquo;, or &ldquo;Service Providers&rdquo;)</li>
                    <li>Create a new SAML 2.0 application/integration</li>
                    <li>You may need to select &ldquo;Custom Application&rdquo; or &ldquo;Manual Setup&rdquo; if prompted for application type</li>
                </ul>
            </Copy>
            <Copy flush>
                <p>When configuring basic application details:</p>
                <ul>
                    <li>App Name: <b>Metro Retro</b></li>
                    <li>App Logo: Use logo from our <a target="_blank" href="https://metroretro.io/media" rel="noreferrer">Media Library</a></li>
                </ul>
            </Copy>
            <Copy flush>
                <p>Note: The exact steps and terminology may vary depending on your Identity Provider. If you encounter any difficulties, consult your IDP&apos;s documentation for setting up SAML 2.0 integrations.</p>
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(1)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

const ConfigureServiceProviderStep: React.FC<StepProps> = ({ gotoStep }) => {
    const { entityId, acsUrl } = useSingleSignOnConstants();

    return (
        <SetupStep title="Step 2 — Configure Service Provider Details">
            <Copy flush>
                <p>
                    In your Identity Provider, you&apos;ll need to configure how it communicates with Metro Retro.
                    Enter the following Metro Retro details into your IDP configuration:
                </p>
            </Copy>
            <Flexer>
                <Input
                    label="Assertion Consumer Service (ACS) URL:"
                    value={acsUrl}
                    after={() => <InputCopyButton value={acsUrl} />}
                    hint="The endpoint where your IDP will send SAML assertions after authentication."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Service Provider Entity ID (Audience):"
                    value={entityId}
                    after={() => <InputCopyButton value={entityId} />}
                    hint="The unique identifier for Metro Retro in SAML communications."
                />
            </Flexer>
            <Copy flush>
                <p>Additional configuration notes:</p>
                <ul>
                    <li>Name ID Format: <b>Email Address</b> (or urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress)</li>
                    <li>Response/Assertion Signature: <b>Signed</b></li>
                    <li>SAML Request Binding: <b>HTTP-Redirect</b> (if prompted)</li>
                    <li>SAML Response Binding: <b>HTTP-POST</b> (if prompted)</li>
                </ul>
            </Copy>
            <Copy flush>
                <p>Configure attribute mappings in your IDP to send the following user attributes:</p>
                <ul>
                    <li>First Name <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> firstName (or appropriate attribute name in your IDP)</li>
                    <li>Last Name <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> lastName (or appropriate attribute name in your IDP)</li>
                    <li>Email <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> email (or appropriate attribute name in your IDP)</li>
                </ul>
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(2)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

const ConfigureIdentityProviderStep: React.FC<StepProps> = ({ form, mutation, gotoStep }) => {
    const { draft, draftDirty, patchDraft } = form;

    useEffect(() => {
        mutation.reset();
    }, [draft]);

    const onSave = () => {
        mutation.mutate(draft);
    };

    return (
        <SetupStep title="Step 3 — Configure Identity Provider Details">
            <Copy flush>
                <p>
                    Now you need to gather information from your Identity Provider to configure Metro Retro.
                    Locate the following details in your IDP&apos;s SAML configuration or metadata:
                </p>
            </Copy>
            <Flexer>
                <Input
                    label="Identity Provider Single Sign-On URL:"
                    value={draft.entryPoint}
                    error={form.errors.entryPoint}
                    onChange={x => patchDraft({ entryPoint: x })}
                    hint="The endpoint Metro Retro will redirect users to for authentication."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Identity Provider Issuer (Entity ID):"
                    value={draft.issuer}
                    error={form.errors.issuer}
                    onChange={x => patchDraft({ issuer: x })}
                    hint="The unique identifier of your Identity Provider."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="X.509 Certificate:"
                    multiline={{ rows: 3, resize: 'vertical' }}
                    value={draft.certificate}
                    error={form.errors.certificate}
                    onChange={x => patchDraft({ certificate: x })}
                    hint="The certificate your IDP uses to sign SAML assertions."
                />
            </Flexer>
            <Copy flush>
                <p>
                    Note: You can typically find these values in your IDP&apos;s application configuration page or by downloading the SAML metadata XML file.
                    If your IDP provides a metadata URL instead, you may need to open it in a browser and extract these values from the XML.
                </p>
            </Copy>
            <Flexer>
                <ResultInfoForMutation mutation={mutation} onSuccess="Settings saved successfully." />
            </Flexer>
            {!mutation.isSuccess && <Flexer flex="row" justify="end">
                <Button disabled={mutation.isLoading || !draftDirty} onAction={onSave}>
                    Save
                </Button>
            </Flexer>}
            {mutation.isSuccess && <>
                <Flexer flex="row" justify="end">
                    <Button variant="primary" onAction={() => gotoStep(3)}>
                        Next
                    </Button>
                </Flexer>
            </>}
        </SetupStep>
    );
};


export const SetupOtherSingleSignOnView = React.memo(() => {
    const nav = useResolve(AppNavigator);
    const [activeStep, setActiveStep] = useState(0);
    const steps = 4;

    const form = useSingleSignOnForm();
    const mutation = useSingleSignOnMutation();

    const gotoStep = (step: number | 'end') => {
        if (step === 'end') {
            nav.go('~/manage/sso/v2');
        }
        else if (step >= 0 && step < steps) {
            setActiveStep(step);
        }
    };

    const stepProps: StepProps = {
        form,
        mutation,
        currentStep: activeStep,
        gotoStep,
    };

    return <Flexer flex="column" gap="2" maxWidth="54rem">
        <Title size="lg" color={Colors.text}>Custom SSO Setup</Title>
        <SetupStepViewer steps={steps} activeIndex={activeStep} onStep={gotoStep} />
        {activeStep === 0 && <CreateAppStep {...stepProps} />}
        {activeStep === 1 && <ConfigureServiceProviderStep {...stepProps} />}
        {activeStep === 2 && <ConfigureIdentityProviderStep {...stepProps} />}
        {activeStep === 3 && <CommonFinalStep {...stepProps} />}
    </Flexer>;
});
